import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const S = {
  burgundy:    '#800020',
  burgundy2:   '#722f37',
  textPrimary: '#1e293b',
  textSecondary:'#475569',
  textMuted:   '#64748b',
  borderLight: '#e2e8f0',
  bgCard:      '#f8fafc',
  success:     '#10b981',
};

const QUICK_ACTIONS = [
  { label: 'Summarize claim',        prompt: 'Give me a concise summary of this claim including key dates, parties, current status, and any open issues.' },
  { label: 'Coverage analysis',      prompt: 'Based on the policy details and claim facts, analyze whether this loss is covered. Identify any potential exclusions or coverage issues.' },
  { label: 'Draft status letter',    prompt: 'Draft a professional status update letter to the claimant summarizing the current status of their claim and next steps.' },
  { label: 'Reserve recommendation', prompt: 'Based on all available claim information, provide a reserve recommendation with detailed justification.' },
  { label: 'Missing information',    prompt: 'What information or documents are missing from this claim file that would be needed to make a decision?' },
  { label: 'Fraud indicators',       prompt: 'Review this claim for any indicators of potential fraud or inconsistencies that warrant further investigation.' },
];

// ─── Simple markdown renderer ─────────────────────────────
function renderMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^### (.+)$/gm, '<h4 style="margin:8px 0 4px;font-size:13px;font-weight:700;color:#1e293b">$1</h4>')
    .replace(/^## (.+)$/gm,  '<h3 style="margin:8px 0 4px;font-size:14px;font-weight:700;color:#1e293b">$1</h3>')
    .replace(/^# (.+)$/gm,   '<h2 style="margin:8px 0 4px;font-size:15px;font-weight:700;color:#1e293b">$1</h2>')
    .replace(/^- (.+)$/gm,   '<div style="padding:2px 0 2px 12px;position:relative"><span style="position:absolute;left:2px;color:#800020">•</span>$1</div>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

// ─── Message bubble ───────────────────────────────────────
function Message({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div style={{ display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row', gap: 8, marginBottom: 14, alignItems: 'flex-start' }}>
      {/* Avatar */}
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: isUser ? '#e2e8f0' : `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700,
        color: isUser ? S.textSecondary : '#fff',
      }}>
        {isUser ? 'You' : 'R'}
      </div>

      {/* Bubble */}
      <div style={{
        maxWidth: '82%',
        background: isUser ? '#f1f5f9' : '#fff',
        border: `1px solid ${isUser ? '#e2e8f0' : 'rgba(128,0,32,0.12)'}`,
        borderRadius: isUser ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
        padding: '10px 12px',
        fontSize: 13,
        color: S.textPrimary,
        lineHeight: 1.55,
      }}>
        {isUser
          ? msg.content
          : <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(msg.content)) }} />
        }
      </div>
    </div>
  );
}

// ─── Typing indicator ─────────────────────────────────────
function TypingIndicator() {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'flex-start' }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>R</div>
      <div style={{ background: '#fff', border: '1px solid rgba(128,0,32,0.12)', borderRadius: '4px 12px 12px 12px', padding: '12px 16px', display: 'flex', gap: 4, alignItems: 'center' }}>
        {[0,1,2].map(i => (
          <span key={i} style={{
            width: 6, height: 6, borderRadius: '50%', background: S.burgundy,
            animation: 'reggieTyping 1.2s infinite',
            animationDelay: `${i * 0.2}s`,
            display: 'inline-block',
          }} />
        ))}
        <style>{`@keyframes reggieTyping { 0%,80%,100%{opacity:.2;transform:scale(.8)} 40%{opacity:1;transform:scale(1)} }`}</style>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// REGGIE PANEL
// ═══════════════════════════════════════════════════════════
const ReggiePanel = ({ claimId, claimNumber, isOpen, onClose }) => {
  const [messages, setMessages]           = useState([]);
  const [input, setInput]                 = useState('');
  const [loading, setLoading]             = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [configured, setConfigured]       = useState(true);
  const [conversations, setConversations] = useState([]);
  const [showConvList, setShowConvList]   = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Welcome message when panel opens for this claim
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `I have full context of claim **${claimNumber || claimId}** including all documents, notes, and status history. How can I help you?`,
      }]);
    }
  }, [isOpen, claimId]);

  // Load previous conversations
  useEffect(() => {
    if (!isOpen || !claimId) return;
    axios.get(`${BASE_URL}/claims/${claimId}/reggie`, { withCredentials: true })
      .then(r => setConversations(r.data?.data || []))
      .catch(() => {});
  }, [isOpen, claimId]);

  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);

    try {
      const { data } = await axios.post(
        `${BASE_URL}/claims/${claimId}/reggie`,
        { message: msg, conversationId },
        { withCredentials: true }
      );
      setConversationId(data.data.conversationId);
      setMessages(prev => [...prev, { role: 'assistant', content: data.data.response }]);
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Something went wrong. Please try again.';
      if (errMsg.toLowerCase().includes('not configured')) setConfigured(false);
      setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${errMsg}` }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, claimId, conversationId]);

  const loadConversation = async (conv) => {
    setConversationId(conv._id);
    setMessages(conv.messages || []);
    setShowConvList(false);
  };

  const newConversation = () => {
    setConversationId(null);
    setMessages([{
      role: 'assistant',
      content: `New conversation started. I have full context of claim **${claimNumber || claimId}**. How can I help?`,
    }]);
    setShowConvList(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay (mobile) */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 199, background: 'transparent' }} />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 360, zIndex: 200,
        background: '#fff',
        borderLeft: '1px solid rgba(148,163,184,0.2)',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.08)',
        display: 'flex', flexDirection: 'column',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Inter', sans-serif",
      }}>

        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${S.borderLight}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 14 }}>R</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>Reggie</div>
                <div style={{ fontSize: 11, color: S.textMuted }}>AI Claims Assistant</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {/* Conversations dropdown toggle */}
              <button
                onClick={() => setShowConvList(v => !v)}
                title="Previous conversations"
                style={{ border: `1px solid ${S.borderLight}`, background: '#f8fafc', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 11, color: S.textSecondary }}
              >History</button>
              <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: S.textMuted, lineHeight: 1, padding: '0 2px' }}>✕</button>
            </div>
          </div>

          {/* Claim badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(128,0,32,0.06)', border: '1px solid rgba(128,0,32,0.15)', borderRadius: 8, padding: '4px 10px', fontSize: 11, color: S.burgundy, fontWeight: 600 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: S.success, flexShrink: 0 }} />
            {claimNumber || claimId}
          </div>
        </div>

        {/* History dropdown */}
        {showConvList && (
          <div style={{ borderBottom: `1px solid ${S.borderLight}`, background: S.bgCard, padding: '10px 16px', flexShrink: 0, maxHeight: 200, overflowY: 'auto' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Conversations</div>
            <button onClick={newConversation} style={{ width: '100%', textAlign: 'left', border: `1px dashed ${S.borderLight}`, background: '#fff', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: S.burgundy, marginBottom: 6, fontWeight: 600 }}>+ New conversation</button>
            {conversations.map(c => (
              <div key={c._id} onClick={() => loadConversation(c)} style={{ padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, color: S.textSecondary, marginBottom: 4, background: c._id === conversationId ? 'rgba(128,0,32,0.06)' : '#fff', border: `1px solid ${S.borderLight}` }}>
                <div style={{ fontWeight: 600, color: S.textPrimary }}>{c.user?.name || 'You'}</div>
                <div style={{ color: S.textMuted, fontSize: 11 }}>{new Date(c.createdAt).toLocaleString()} · {c.messages?.length || 0} messages</div>
              </div>
            ))}
          </div>
        )}

        {/* Not configured banner */}
        {!configured && (
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 0, padding: '10px 16px', fontSize: 12, color: '#9a3412', flexShrink: 0 }}>
            Reggie is not configured. Contact your administrator to enable AI features.
          </div>
        )}

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px' }}>
          {messages.map((msg, i) => <Message key={i} msg={msg} />)}
          {loading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick actions */}
        <div style={{ padding: '8px 16px', borderTop: `1px solid ${S.borderLight}`, flexShrink: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Quick Actions</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {QUICK_ACTIONS.map(a => (
              <button
                key={a.label}
                onClick={() => send(a.prompt)}
                disabled={loading}
                style={{
                  border: `1px solid ${S.borderLight}`, background: '#f8fafc', borderRadius: 8,
                  padding: '5px 10px', cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 11, color: S.textSecondary, fontFamily: 'inherit',
                  opacity: loading ? 0.5 : 1,
                }}
              >{a.label}</button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div style={{ padding: '12px 16px 16px', borderTop: `1px solid ${S.borderLight}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type a question... (Enter to send)"
              disabled={loading || !configured}
              rows={2}
              style={{
                flex: 1, resize: 'none', border: `1px solid ${S.borderLight}`, borderRadius: 10,
                padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
                color: S.textPrimary, background: '#f8fafc', outline: 'none',
                lineHeight: 1.4,
              }}
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim() || !configured}
              style={{
                width: 38, height: 38, borderRadius: 10, border: 'none', cursor: 'pointer',
                background: loading || !input.trim() ? '#e2e8f0' : `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                color: loading || !input.trim() ? '#94a3b8' : '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                fontSize: 16, transition: 'all 0.15s',
              }}
            >→</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReggiePanel;
